import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { deliveryTasksKeys } from "@/application/query-keys/delivery-tasks.keys";
import { sprintsKeys } from "@/application/query-keys/sprints.keys";
import {
  sprintsRepository,
  type CreateSprintInput,
} from "@/infrastructure/repositories/sprints.supabase.repository";
import type { SprintRow } from "@/infrastructure/supabase/database.types";

export function useSprints() {
  return useQuery({
    queryKey: sprintsKeys.list(),
    queryFn: () => sprintsRepository.list(),
  });
}

export function useCreateSprint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSprintInput) => sprintsRepository.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sprintsKeys.all });
    },
  });
}

export function useUpdateSprint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: Partial<Pick<SprintRow, "name" | "start_date" | "end_date">>;
    }) => sprintsRepository.update(id, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sprintsKeys.all });
    },
  });
}

/** Deleting a sprint returns its tasks to the backlog, so invalidate both. */
export function useDeleteSprint() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => sprintsRepository.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sprintsKeys.all });
      queryClient.invalidateQueries({ queryKey: deliveryTasksKeys.all });
    },
  });
}
